import React, { useState, useEffect } from "react";
import { Bell, Search, Menu, Moon, Sun, Plus, ChevronsLeft, ChevronsRight, Settings, LogOut, User, Building2, Check, KeyRound, Shield } from "lucide-react";
import Logo from "@/components/brand/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { endpoints, getTenantScope, setTenantScope, getActiveAccountEmail, setActiveAccountEmail } from "@/lib/api";
import { toast } from "sonner";

export default function TopNav({ onToggleSidebar, onToggleCollapse, collapsed, dark, onToggleTheme }) {
  const [user, setUser] = useState({ name: "", role: "", avatar_initials: "" });
  const [notifCount, setNotifCount] = useState(3);
  const [clients, setClients] = useState([]);
  const [scope, setScope] = useState(getTenantScope());
  const [accounts, setAccounts] = useState([]);
  const [activeEmail, setActiveEmail] = useState(getActiveAccountEmail());
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    // Prefer authenticated identity; fall back to legacy /me
    endpoints.authMe()
      .then((me) => setUser({ name: me.full_name || me.email, role: (me.role || "").replace("_", " "), avatar_initials: ((me.full_name || me.email || "??").split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join("")) || "??" }))
      .catch(() => endpoints.me().then(setUser).catch(() => {}));
    endpoints.listNotifications().then((n) => setNotifCount(n.filter((x) => !x.read).length)).catch(() => {});
    endpoints.listClients().then(setClients).catch(() => {});
    endpoints.listAccounts().then(setAccounts).catch(() => setAccounts([]));
    endpoints.myPermissions().then(setPermissions).catch(() => {});
    const onChange = () => setScope(getTenantScope());
    const onAcc = () => {
      setActiveEmail(getActiveAccountEmail());
      endpoints.myPermissions().then(setPermissions).catch(() => {});
      endpoints.listAccounts().then(setAccounts).catch(() => {});
      endpoints.authMe().then((me) => setUser({ name: me.full_name || me.email, role: (me.role || "").replace("_", " "), avatar_initials: ((me.full_name || me.email || "??").split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join("")) || "??" })).catch(() => {});
    };
    window.addEventListener("isoquest:scope-changed", onChange);
    window.addEventListener("isoquest:account-changed", onAcc);
    window.addEventListener("isoquest:auth-changed", onAcc);
    return () => {
      window.removeEventListener("isoquest:scope-changed", onChange);
      window.removeEventListener("isoquest:account-changed", onAcc);
      window.removeEventListener("isoquest:auth-changed", onAcc);
    };
  }, []);

  const signInAsAccount = (acc) => {
    setActiveAccountEmail(acc.email);
    setTenantScope(acc.client_slug ? { role: "client", slug: acc.client_slug, company_name: acc.client_slug } : null);
    toast.success(`Signed in as ${acc.full_name}`);
    setTimeout(() => { window.location.reload(); }, 250);
  };
  const clearIdentity = () => {
    setActiveAccountEmail(null);
    setTenantScope(null);
    toast.success("Reset to admin");
    setTimeout(() => { window.location.href = "/dashboard"; }, 200);
  };

  const signInAs = (client) => {
    setTenantScope({ role: "client", slug: client.slug, company_name: client.company_name });
    toast.success(`Signed in as ${client.company_name}`);
    setTimeout(() => { window.location.href = `/clients/${client.slug}`; }, 200);
  };
  const signOut = () => {
    import("@/lib/auth").then(({ auth }) => {
      auth.logout();
      setTenantScope(null);
      toast.success("Signed out");
      setTimeout(() => { window.location.href = "/login"; }, 150);
    });
  };

  const currentAcc = activeEmail ? accounts.find(a => a.email === activeEmail) : null;
  const isClient = scope?.role === "client" || currentAcc?.role === "client_admin" || currentAcc?.role === "client_employee";
  const activeName = isClient ? (scope.company_name || scope.slug) : user.name;
  const activeSubtitle = isClient ? "Client user" : user.role;
  const activeInitials = isClient
    ? (scope.company_name || scope.slug || "??").split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join("")
    : (user.avatar_initials || "AM");

  return (
    <header
      data-testid="top-navbar"
      className="fixed top-0 left-0 right-0 z-50 h-16 glass-card border-b border-slate-200/60 dark:border-white/10 flex items-center px-3 sm:px-5"
    >
      <div className="flex items-center gap-2 md:gap-3">
        <Button
          data-testid="mobile-menu-button"
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          className="md:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <Logo />
        <Button
          data-testid="sidebar-toggle-button"
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="hidden md:inline-flex ml-2 text-slate-500"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex-1 max-w-xl mx-4 hidden sm:block">
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
          <Input
            data-testid="top-navbar-search-input"
            placeholder="Search tasks, clients, audits..."
            className="pl-9 pr-16 bg-white/70 dark:bg-slate-900/60 border-slate-200/70 dark:border-white/10 h-10 rounded-xl"
          />
          <kbd className="hidden md:inline-flex absolute right-2.5 top-1/2 -translate-y-1/2 items-center gap-1 rounded-md border border-slate-200 dark:border-white/10 bg-slate-100/80 dark:bg-slate-800/80 px-1.5 py-0.5 text-[10px] font-mono text-slate-500">
            ⌘ K
          </kbd>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <Button
          data-testid="theme-toggle-button"
          variant="ghost"
          size="icon"
          onClick={onToggleTheme}
          className="rounded-full text-slate-500"
          title="Toggle theme"
        >
          {dark ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid="top-navbar-notifications-button"
              variant="ghost"
              size="icon"
              className="rounded-full text-slate-500 relative"
            >
              <Bell className="h-4.5 w-4.5" />
              {notifCount > 0 && (
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900 animate-pulse" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="flex items-center justify-between">
              Notifications
              <Badge variant="secondary" className="ml-2">{notifCount} new</Badge>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-xs text-slate-500">
              Open the Notifications panel on your dashboard for details.
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="top-navbar-user-profile-button"
              className="ml-1 flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Avatar className="h-8 w-8 ring-2 ring-indigo-500/30">
                <AvatarFallback className={`text-white text-xs font-semibold ${isClient ? "bg-gradient-to-br from-amber-500 to-orange-600" : "brand-gradient"}`}>
                  {activeInitials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden lg:block text-left leading-tight">
                <div className="text-[13px] font-semibold truncate max-w-[160px]">{activeName}</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[160px]">{activeSubtitle}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80" data-testid="user-menu">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>{currentAcc?.full_name || activeName}</span>
              {isClient && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Client</Badge>}
              {permissions?.role && !isClient && <Badge variant="outline" className="text-[10px]">{permissions.role}</Badge>}
            </DropdownMenuLabel>
            {permissions && (
              <div className="px-2 pb-1 text-[10px] text-slate-500 flex flex-wrap gap-1">
                <Badge variant="outline" className="text-[10px]">{permissions.allowed_modules?.length ?? 0} modules</Badge>
                {permissions.can_write ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">write</Badge> : <Badge variant="outline" className="text-[10px] bg-slate-50">read-only</Badge>}
                {permissions.can_invite_users && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">invite</Badge>}
              </div>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1">
              <KeyRound className="h-3 w-3" /> Sign in as team member
            </DropdownMenuLabel>
            <DropdownMenuGroup className="max-h-40 overflow-y-auto">
              {accounts.filter(a => ["admin", "staff", "consultant"].includes(a.role)).map((a) => (
                <DropdownMenuItem
                  key={a.id}
                  data-testid={`sign-in-as-account-${a.id}`}
                  onClick={() => signInAsAccount(a)}
                  className="cursor-pointer"
                >
                  <Shield className="mr-2 h-4 w-4 text-indigo-500" />
                  <span className="flex-1 truncate">{a.full_name}</span>
                  <span className="text-[10px] text-slate-500 mr-1">{a.role}</span>
                  {activeEmail === a.email && <Check className="h-4 w-4 text-emerald-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
              Sign in as a client
            </DropdownMenuLabel>
            <DropdownMenuGroup className="max-h-40 overflow-y-auto">
              {clients.length === 0 && (
                <div className="px-2 py-2 text-xs text-slate-500">No clients available</div>
              )}
              {clients.map((c) => (
                <DropdownMenuItem
                  key={c.slug}
                  data-testid={`sign-in-as-${c.slug}`}
                  onClick={() => signInAs(c)}
                  className="cursor-pointer"
                >
                  <Building2 className="mr-2 h-4 w-4 text-indigo-500" />
                  <span className="flex-1 truncate">{c.company_name}</span>
                  {isClient && scope?.slug === c.slug && <Check className="h-4 w-4 text-emerald-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="sign-out-session" onClick={signOut} className="cursor-pointer text-rose-600 focus:text-rose-700">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
            {(isClient || (activeEmail && activeEmail !== permissions?.email)) ? (
              <DropdownMenuItem data-testid="sign-out-of-client" onClick={clearIdentity} className="cursor-pointer text-indigo-700">
                <LogOut className="mr-2 h-4 w-4" /> Reset to admin view
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
